import { Observable } from "rxjs";

import { ProductsRepository, ProductScheduleSlotsRepository, OrdersRepository } from "lib/repositories/index";
import { OrderFullDecorator } from "lib/decorators/index";
import st from "lib/utils/index";
import { createAlert } from "modules/app/actions";
import { createFormInstance, createResourceInstance } from "modules/utils/index";
import { goToOrder, processPayment } from "modules/orders/utils";
import NewOrderProductModalDuck from "./NewOrderProductModal/index";
import { changeCurrentCompany } from "modules/auth/index";

const mapOrderToDataForBuild = (order) => ({
  ...order,
  order_items: order.order_items.filter((item) => item.item_type === "product_custom"),
});

export const OPEN = "orders/NewOrderModal/OPEN";
export const CLOSE = "orders/NewOrderModal/CLOSE";

export const SET_STEP = "orders/NewOrderModal/SET_STEP";
export const SET_STEP_START = "orders/NewOrderModal/SET_STEP_START";
export const SET_STEP_SUCCESS = "orders/NewOrderModal/SET_STEP_SUCCESS";
export const SET_STEP_FAILURE = "orders/NewOrderModal/SET_STEP_FAILURE";
export const SUBMIT = "orders/NewOrderModal/SUBMIT";
export const SET_STEP_LOADING = "orders/NewOrderModal/SET_STEP_LOADING";

export const INITIALIZE_ORDER = "orders/NewOrderModal/INITIALIZE_ORDER";
export const UPDATE_ORDER = "orders/NewOrderModal/UPDATE_ORDER";
export const VALIDATE = "orders/NewOrderModal/VALIDATE";
export const FETCH_SUBMIT = "orders/NewOrderModal/FETCH_SUBMIT";
export const FETCH_SUBMIT_START = "orders/NewOrderModal/FETCH_SUBMIT_START";
export const FETCH_SUBMIT_SUCCESS = "orders/NewOrderModal/FETCH_SUBMIT_SUCCESS";
export const FETCH_SUBMIT_FAILURE = "orders/NewOrderModal/FETCH_SUBMIT_FAILURE";

export const FETCH_SLOTS_DATA = "orders/NewOrderModal/FETCH_SLOTS_DATA";
export const FETCH_SLOTS_DATA_START = "orders/NewOrderModal/FETCH_SLOTS_DATA_START";
export const FETCH_SLOTS_DATA_SUCCESS = "orders/NewOrderModal/FETCH_SLOTS_DATA_SUCCESS";
export const FETCH_SLOTS_DATA_FAILURE = "orders/NewOrderModal/FETCH_SLOTS_DATA_FAILURE";

export const REQUEST_CALCULATE = "orders/NewOrderModal/REQUEST_CALCULATE";
export const FETCH_CALCULATE = "orders/NewOrderModal/FETCH_CALCULATE";
export const FETCH_CALCULATE_START = "orders/NewOrderModal/FETCH_CALCULATE_START";
export const FETCH_CALCULATE_SUCCESS = "orders/NewOrderModal/FETCH_CALCULATE_SUCCESS";
export const FETCH_CALCULATE_FAILURE = "orders/NewOrderModal/FETCH_CALCULATE_FAILURE";

export const STEP_PRODUCT = 1;
export const STEP_SCHEDULE = 2;
export const STEP_ORDER_ITEMS = 3;
export const STEP_CUSTOMER_DETAILS = 4;
export const STEP_PAYMENTS = 5;
export const STEPS = {
  STEP_PRODUCT,
  STEP_SCHEDULE,
  STEP_ORDER_ITEMS,
  STEP_CUSTOMER_DETAILS,
  STEP_PAYMENTS,
};

export const stateSelector = (state) => state.orders.NewOrderModal;

const slotsDataInstance = createResourceInstance({
  FETCH: FETCH_SLOTS_DATA,
  FETCH_START: FETCH_SLOTS_DATA_START,
  FETCH_SUCCESS: FETCH_SLOTS_DATA_SUCCESS,
  FETCH_FAILURE: FETCH_SLOTS_DATA_FAILURE,
  stateSelector,
  stateKey: "slotsData",
  getModel: ({ form: { model: order } }) => {
    if (order.product_variant) {
      // force cut off time to be enabled on new order calendar
      return ProductScheduleSlotsRepository.findAll({
        product_variant_id: order.product_variant.id,
        hide_cutofftime: true,
      });
    } else {
      return Observable.of([]).delay(0);
    }
  },
});

const formInstance = createFormInstance({
  INITIALIZE_MODEL: INITIALIZE_ORDER,
  UPDATE_MODEL: UPDATE_ORDER,
  VALIDATE,
  SUBMIT: FETCH_SUBMIT,
  FETCH_SUBMIT_START,
  FETCH_SUBMIT_SUCCESS,
  FETCH_SUBMIT_FAILURE,
  stateSelector,
  stateKey: "form",
  isDirty: () => true,
  withSuccessAlert: false,
  validate: ({ step, form: { model: order } }, errors, { submitValidate }) => {
    if (step === STEP_PRODUCT) {
      errors.add(`order.product_name`, st.validators.presence(order.product_name));
    } else if (step === STEP_SCHEDULE) {
      const slot = order.product_schedule_slot;
      if (slot && !slot.id) {
        submitValidate && errors.add(`order.product_schedule_slot.start_date`, st.validators.presence(slot.start_date));
        submitValidate && errors.add(`order.product_schedule_slot.end_date`, st.validators.presence(slot.end_date));
        if (slot.start_time || slot.end_time) {
          submitValidate &&
            errors.add(`order.product_schedule_slot.start_time`, st.validators.presence(slot.start_time));
          submitValidate && errors.add(`order.product_schedule_slot.end_time`, st.validators.presence(slot.end_time));
        }
      }
      submitValidate &&
        slot.starts_at &&
        slot.ends_at &&
        errors.add(
          `order.product_schedule_slot.end_time`,
          st.validators.range(new Date(slot.ends_at), {
            greater_than_or_equal_to: new Date(slot.starts_at),
          })
        );
    } else if (step === STEP_ORDER_ITEMS) {
      order.ordered_product_pricelist_items.forEach((orderedItem) => {
        errors.add(
          `order.ordered_product_pricelist_items[${orderedItem.uuid}].persons_count`,
          st.validators.range(orderedItem.persons_count, {
            greater_than_or_equal_to: orderedItem.min_persons_count,
            less_than_or_equal_to: orderedItem.max_persons_count,
          })
        );
      });
      order.priced_ordered_fields.forEach((orderedField) => {
        // orderedField.product_order_field.order_value_required && errors.add(`order.ordered_product_order_fields[${orderedField.uuid}].value`, st.validators.presence(orderedField.value))
      });
      order.order_customer_participants.forEach((participant) => {
        participant.priced_ordered_fields.forEach((orderedField) => {
          // orderedField.product_order_field.order_value_required && errors.add(`order.order_customer_participants[${participant.uuid}].ordered_product_order_fields[${orderedField.uuid}].value`, st.validators.presence(orderedField.value))
        });
      });
    } else if (step === STEP_CUSTOMER_DETAILS) {
      if (!st.authorize.company.reseller) {
        errors.add(
          "base",
          st.validators.presence(order.customer_display_name) &&
            I18n.t("activerecord.errors.models.order.customer_display_name_missing")
        );
      }

      order.customer_ordered_fields.forEach((orderedField) => {
        if (orderedField.product_order_field.preset_id === "customer_full_name") {
          errors.add(
            `order.ordered_product_order_fields[${orderedField.uuid}].value`,
            st.validators.length(orderedField.value, {
              minimum: 5,
              maximum: 100,
            })
          );
        }
      });
      if (st.authorize.company.reseller || st.authorize.company.agent) {
        order.customer_ordered_fields.forEach((orderedField) => {
          if (
            orderedField.product_order_field.preset_id === "customer_full_name" ||
            orderedField.product_order_field.preset_id === "customer_email"
          ) {
            errors.add(
              `order.ordered_product_order_fields[${orderedField.uuid}].value`,
              st.validators.presence(orderedField.value) &&
                I18n.t("papp.components.ProductCustomerOrderFieldFormSection.value_required")
            );
          }
        });
      }
    } else if (step === STEP_PAYMENTS) {
      const MODE_PAY_NOW = "MODE_PAY_NOW";
      submitValidate &&
        order.isReseller &&
        order._NewOrderPaymentsMode !== MODE_PAY_NOW &&
        errors.add(`order.payment_profile_name`, st.validators.presence(order.payment_profile_name));
      if (st.authorize.company.reseller || st.authorize.company.agent) {
        submitValidate &&
          order.isReseller &&
          !st.authorize.company.payment_terminal &&
          errors.add(`order.notify_customer`, st.validators.presence(order.notify_customer));

        submitValidate && errors.add(`order.assigned_creator_id`, st.validators.presence(order.assigned_creator_id));
      }
      submitValidate && errors.add(`order.status`, st.validators.presence(order.status));
      submitValidate &&
        errors.add(
          "base",
          st.validators.presence(order._NewOrderPaymentsMode) &&
            I18n.t("papp.orders.NewOrderPaymentsForm.choose_payments_mode_error")
        );
    }
  },
  submitModel: ({ form: { model: order, initialModel: initialOrder }, step }, store) => {
    let update$;
    if (step === STEP_PRODUCT && order.booking_type === "static_date_booking") {
      update$ = Observable.of(order);
    } else if (step <= STEP_SCHEDULE) {
      update$ = OrdersRepository.calculate(mapOrderToDataForBuild(order));
    } else if (step === STEP_ORDER_ITEMS) {
      update$ = OrdersRepository.create(mapOrderToDataForBuild(order));
    } else if (step === STEP_CUSTOMER_DETAILS) {
      const mapOrderToData = (order) => ({
        id: order.id,
        token: order.token,
        customer_locale: order.customer_locale,
        ordered_product_order_fields: order.customer_ordered_fields,
        order_customer_participants: order.order_customer_participants.map((participant) => ({
          id: participant.id,
          ordered_product_order_fields: participant.customer_ordered_fields,
        })),
      });
      update$ = OrdersRepository.updateCustomerDetails(mapOrderToData(order), mapOrderToData(initialOrder));
    } else if (step === STEP_PAYMENTS) {
      const mapOrderToData = (order) => ({
        id: order.id,
        token: order.token || null,
        status: order.status,
        notify_customer: order.notify_customer,
        notify_supplier: st.authorize.company.reseller || st.authorize.company.agent ? true : undefined,
        assigned_creator_id: order.assigned_creator_id,
        internal_note: order.internal_note,
        supplier_note: st.authorize("update_supplier_note", order) ? order.supplier_note : undefined,
        agent_note: st.authorize("update_agent_note", order) ? order.agent_note : undefined,
        st_note: st.authorize("update_st_note", order) ? order.st_note : undefined,
        additional_information: order.additional_information,
        // agent_commision_amount: order.agent_commision_amount,
        // saas_commision_amount: order.saas_commision_amount,
        payment_profile_id: order.payment_profile_id,
        cancellation_policy: order.cancellation_policy,
        installments: order.installments,
        order_transfers: order.order_transfers,
        order_items: order.order_items.filter((item) => item.uuid === "ppServiceFee"),
      });
      update$ = OrdersRepository.update(mapOrderToData(order), mapOrderToData(initialOrder));
    } else {
      throw new Error(`Unknown step: ${step}`);
    }
    return update$;
  },
});

const calculateInstance = createResourceInstance({
  FETCH: FETCH_CALCULATE,
  FETCH_START: FETCH_CALCULATE_START,
  FETCH_SUCCESS: FETCH_CALCULATE_SUCCESS,
  FETCH_FAILURE: FETCH_CALCULATE_FAILURE,
  stateSelector,
  stateKey: "calculate",
  getModel: ({ form: { model: order, initialModel: initialOrder } }) => {
    return OrdersRepository.calculate(
      {
        ...order,
        order_items: order.order_items.filter((item) => item.item_type === "product_custom"),
      },
      {
        ...initialOrder,
        order_items: initialOrder.order_items.filter((item) => item.item_type === "product_custom"),
      }
    );
  },
});

const initialState = {
  slotsData: slotsDataInstance.initialState,
  form: formInstance.initialState,
  calculate: calculateInstance.initialState,
  opened: false,
  step: null,
  stepLoading: false,
};

const reducers = {
  ...slotsDataInstance.reducersMap,
  ...formInstance.reducersMap,
  ...calculateInstance.reducersMap,

  [OPEN]: (state) => {
    return {
      ...initialState,
      opened: true,
    };
  },

  [CLOSE]: (state) => {
    return {
      ...state,
      opened: false,
    };
  },

  [SET_STEP_START]: (state) => {
    return {
      ...state,
      stepLoading: true,
    };
  },

  [SET_STEP_SUCCESS]: (state, { step }) => {
    return {
      ...state,
      step,
      stepLoading: false,
    };
  },

  [SET_STEP_FAILURE]: (state) => {
    return {
      ...state,
      stepLoading: false,
    };
  },

  [SET_STEP_LOADING]: (state) => {
    return {
      ...state,
      stepLoading: true,
    };
  },

  [REQUEST_CALCULATE]: (state) => ({
    ...state,
    calculate: {
      ...state.calculate,
      fetchLoading: true,
    },
  }),
};

const epics = [
  ...slotsDataInstance.epics,
  ...formInstance.epics,
  ...calculateInstance.epics,

  (actions$) =>
    actions$.ofType(OPEN).map(({ order }) => {
      return loadOrder(order);
    }),

  // changing the step
  (actions$, { getState }) =>
    actions$
      .ofType(OPEN)
      .merge(actions$.ofType(SET_STEP))
      .map(({ step }) => {
        console.log(step);
        const {
          form: { model: order },
          step: currentStep,
        } = stateSelector(getState());
        let defaultStep = STEP_PRODUCT;
        let maxStep = STEP_PRODUCT;
        if (order.product_variant || order.stock_type) {
          defaultStep = maxStep = STEP_SCHEDULE;
          if (
            order.booking_type === "no_booking" ||
            (order.booking_type === "static_date_booking" && order.product_schedule_slot)
          ) {
            defaultStep = maxStep = STEP_ORDER_ITEMS;
            if (order.id) {
              defaultStep = maxStep = STEP_CUSTOMER_DETAILS;
              if (order.customer_display_name) {
                defaultStep = maxStep = STEP_PAYMENTS;
              }
            }
          }
        }
        let nextStep = Math.min(maxStep, step || currentStep || defaultStep);
        if (order.booking_type === "no_booking" && nextStep === STEP_SCHEDULE) {
          nextStep = STEP_ORDER_ITEMS;
        }
        return { type: SET_STEP_START, step: nextStep };
      }),

  (actions$, store) =>
    actions$.ofType(SET_STEP_START).switchMap(({ step }) => {
      const {
        form: { model: order },
      } = stateSelector(store.getState());

      const abandon$ =
        order.id && step <= STEP_ORDER_ITEMS
          ? OrdersRepository.abandon(order.id, order.token)
              .map(() => loadOrder(OrderFullDecorator.clone(order)))
              .catch((error) => {
                st.api.handleError(error, store);
                return Observable.of({ type: SET_STEP_FAILURE });
              })
          : Observable.empty();

      const setStepSuccess$ = Observable.of({ type: SET_STEP_SUCCESS, step });

      let loadStep$ = setStepSuccess$;

      if (step === STEP_SCHEDULE) {
        const fetchSlots$ = Observable.of(fetchSlotsData());

        const fetchSlotsResponse$ = actions$
          .ofType(FETCH_SLOTS_DATA_SUCCESS)
          .merge(actions$.ofType(FETCH_SLOTS_DATA_FAILURE))
          .take(1)
          .flatMap(({ type }) => {
            return type === FETCH_SLOTS_DATA_SUCCESS ? Observable.empty() : Observable.throw(FETCH_SLOTS_DATA_FAILURE);
          });

        loadStep$ = Observable.concat(fetchSlots$, fetchSlotsResponse$, setStepSuccess$).catch((error) => {
          if (error === FETCH_SLOTS_DATA_FAILURE) {
            return Observable.of({ type: SET_STEP_FAILURE });
          } else {
            return Observable.throw(error);
          }
        });
      } else if (step === STEP_ORDER_ITEMS && order.ordered_product_pricelist_items.length === 0) {
        const calculateOrder$ = OrdersRepository.calculate(mapOrderToDataForBuild(order)).map((order) =>
          loadOrder(order)
        );
        loadStep$ = Observable.concat(calculateOrder$, setStepSuccess$).catch((error) => {
          st.api.handleError(error, store);
          return Observable.of({ type: SET_STEP_FAILURE });
        });
      }

      return Observable.concat(abandon$, loadStep$);
    }),

  (actions$) => actions$.ofType(REQUEST_CALCULATE).debounceTime(1000).mapTo(fetchCalculate()),

  // updateOrder with calculate result, but only if there has been no UPDATE_ORDER action in the meantime
  (actions$) =>
    actions$.ofType(FETCH_CALCULATE).switchMap(() => {
      return actions$
        .ofType(FETCH_CALCULATE_SUCCESS)
        .map(({ model: order }) => updateOrder(order))
        .takeUntil(actions$.ofType(UPDATE_ORDER).merge(actions$.ofType(SET_STEP)).merge(actions$.ofType(SUBMIT)));
    }),

  (actions$, { getState, dispatch }) =>
    actions$.ofType(SUBMIT).switchMap(() => {
      const {
        form: { model: order },
        step,
      } = stateSelector(getState());

      const submit$ = Observable.of(fetchSubmit());

      const submitResult$ = actions$
        .ofType(FETCH_SUBMIT_SUCCESS)
        .merge(actions$.ofType(FETCH_SUBMIT_FAILURE))
        .take(1)
        .flatMap((action) => {
          if (action.type === FETCH_SUBMIT_SUCCESS) {
            return Observable.of(loadOrder(action.model));
          } else {
            return Observable.throw(action.error);
          }
        });

      const isOnline =
        order &&
        order.installments &&
        order.installments.length &&
        order.installments[0].transfer_type === "online_payment" &&
        (order.notify_customer === "false" || order.notify_customer === false);
      let afterSubmit$;
      if (isOnline) {
        afterSubmit$ =
          step < STEP_PAYMENTS
            ? Observable.of(setStep(step + 1))
            : Observable.of(processPayment(order.id, order.token, order), {
                type: SET_STEP_LOADING,
              });
      } else {
        afterSubmit$ =
          step < STEP_PAYMENTS
            ? Observable.of(setStep(step + 1))
            : Observable.of(close({ abandonOrder: false }), goToOrder(order.id, order.token, { refresh: true }));
      }

      return Observable.concat(submit$, submitResult$, afterSubmit$).catch((error) => {
        st.api.handleError(error, { getState, dispatch });
        return Observable.empty();
      });
    }),

  (actions$, { getState, dispatch }) =>
    actions$
      .ofType(CLOSE)
      .filter(({ abandonOrder }) => abandonOrder)
      .flatMap(() => {
        const {
          form: { model: order },
          step,
        } = stateSelector(getState());
        if (order.id) {
          return OrdersRepository.abandon(order.id, order.token)
            .flatMap(() => Observable.empty())
            .catch((error) => {
              st.api.handleError(error, { getState, dispatch });
              return Observable.of(
                createAlert({
                  type: "error",
                  content: I18n.t("papp.orders.NewOrderModal.abandon_failed_message"),
                  timeout: false,
                })
              );
            });
        } else {
          return Observable.empty();
        }
      }),
];

export const { fetch: fetchSlotsData } = slotsDataInstance.actions;
export const {
  initializeModel: loadOrder,
  updateModel: updateOrder,
  validate,
  submit: fetchSubmit,
} = formInstance.actions;
export const { fetch: fetchCalculate } = calculateInstance.actions;

export function open(order) {
  order = OrderFullDecorator.build(order);
  return { type: OPEN, order };
}

export function close({ abandonOrder = true } = {}) {
  return { type: CLOSE, abandonOrder };
}

export function calculatePricing() {
  return { type: REQUEST_CALCULATE };
}

export function setStep(step) {
  return { type: SET_STEP, step };
}

export function goBack() {
  return (dispatch, getState) => {
    const {
      form: { model: order },
      step,
    } = stateSelector(getState());
    let nextStep = step - 1;
    if (order.booking_type === "no_booking" && nextStep === STEPS.STEP_SCHEDULE) {
      nextStep--;
    }
    if (nextStep === STEP_PRODUCT) {
      // Reset the order
      if (order.product_variant) {
        dispatch(loadOrder(OrderFullDecorator.build()));
      }
    }
    dispatch(setStep(nextStep));
  };
}

export function submit() {
  return { type: SUBMIT };
}

export default st.duck.createDuck(initialState, reducers, epics, {
  NewOrderProductModal: NewOrderProductModalDuck,
});



// WEBPACK FOOTER //
// ./src/modules/orders/NewOrderModal/index.js